import * as React from "react";
import {} from "@blueprintjs/core";
import { useCommonHooks } from "../../../hooks";
import { useLanguages } from "../../../hooks/applicationHooks/useLanguages";
import { ActionCode, FormQuestionOperationalEditDto } from "../../../api";
import { useCrudApi } from "../../../hooks/useCrudApi";
import {
  FGCheckboxInput,
  FGTextInput,
  FlexFieldGroup,
  showSuccess,
  useFGFormik,
} from "nsitools-react";
import * as Yup from "yup";
import {
  CardFieldGroup,
  CustomFormGenerator,
  FGTranslations,
} from "../../../components";

export interface FormQuestionOperationalDetailPanelProps {
  formQuestionOperationalId: number;
}

export const FormQuestionOperationalDetailPanel: React.FunctionComponent<
  FormQuestionOperationalDetailPanelProps
> = ({ formQuestionOperationalId }) => {
  const { apis, t, hasAction, validators } = useCommonHooks();
  const { buildDefaultDataTranslations } = useLanguages();

  const isEditable = React.useMemo(
    () => hasAction([ActionCode.EditFormQuestion]),
    [hasAction]
  );

  const { data, isFetching, validationErrors, onSave, isSaving, refetch } =
    useCrudApi({
      queryKey: [
        "form-question-operational-details",
        formQuestionOperationalId,
      ],
      fetchFn: async () => {
        if (formQuestionOperationalId > 0) {
          return apis.formQuestionOperationals.get(formQuestionOperationalId);
        }
        const newQuestion = FormQuestionOperationalEditDto.fromJS({});
        newQuestion.dataTranslation = await buildDefaultDataTranslations();
        newQuestion.isActive = true;

        return newQuestion;
      },
      saveFn: async (toSave: FormQuestionOperationalEditDto) => {
        return apis.formQuestionOperationals.save(toSave);
      },
      onSaved: () => {
        showSuccess(t("FormQuestionOperationalSaved"));
        refetch();
      },
      serverValidationRootKey: "Dto",
    });

  const validationSchema = React.useMemo(
    () =>
      Yup.object().shape({
        code: Yup.string().required(t("Required")).max(150),
        dataTranslation: validators.dataTranslationValidator,
      }),
    [t, validators.dataTranslationValidator]
  );

  const formik = useFGFormik({
    initialValues: data,
    onSubmit: (d) => onSave(d),
    validationSchema: validationSchema,
  });

  return (
    <FlexFieldGroup columns={2}>
      <CardFieldGroup title={t("Details")}>
        <CustomFormGenerator
          initialValues={data}
          loading={isFetching}
          onSubmit={onSave}
          saving={isSaving}
          editable={isEditable}
          editMode={isEditable}
          showDeleteButton={false}
          validationErrors={validationErrors}
          validationSchema={validationSchema}
          formik={formik}
        >
          <FGTextInput name="code" label={t("Code")} forceCase="upper" />
          <FGTranslations label={t("Name")} />
          <FGCheckboxInput name="isActive" label={t("IsActive")} />
        </CustomFormGenerator>
      </CardFieldGroup>
    </FlexFieldGroup>
  );
};

export default FormQuestionOperationalDetailPanel;
